import React, { useState } from "react";

import { compact, filter, isEmpty } from "lodash/fp";

import ToggleButton from "@mui/material/ToggleButton";

import ChangeStatusMenu from "@app/analytics/components/ChangeStatusMenu";
import IconHeading from "@app/analytics/components/IconHeading";
import ViewToggle from "@app/analytics/components/ViewToggle";
import usePulseClips from "@app/analytics/Pulse/hooks/usePulseClips";
import { PulseClip, PulseTopic } from "@app/analytics/Pulse/types";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import { ActionItemClipStatus, UpdateClipOperation } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import ExpandableList from "@rui/ExpandableList";
import { Column, Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import ActionItemWithStatus from "./ActionItemWithStatus";
import { BulletedList, CountChip, TableSubHeading, Text } from "../styles";

const ActionItems = ({
  actionItems,
}: {
  actionItems: PulseTopic["actionItems"];
}) => {
  const [filterBy, setFilterBy] = useState<"all" | "me">("me");
  const [selectedActionItem, setSelectedActionItem] =
    useState<PulseClip | null>(null);

  const { user } = useAuthenticated();
  const {
    statuses,
    actionItems: actionItemClips,
    updateClip,
  } = usePulseClips();

  const {
    anchorElement,
    open: statusMenuOpen,
    onClose: onCloseStatusMenu,
    onClick: onClickStatusChip,
  } = useAnchorElement();

  const handleToggleSelection = (
    _e: React.SyntheticEvent,
    value: "all" | "me"
  ) => {
    if (!value) return;
    setFilterBy(value);
  };

  const actionItemsForView =
    filterBy === "me"
      ? filter({ ownerUserId: user.id }, actionItems)
      : actionItems;

  const actionItemsToShow = compact(
    actionItemsForView.map((ai) => {
      const actionItemClip = actionItemClips[ai.id];
      // if there's no matching action item clip, that means
      // it was already rejected
      if (!actionItemClip) return null;
      const status = actionItemClip?.status ?? "read_inactive";
      const clip: PulseClip = {
        ...ai,
        status,
        statusDescription: statuses?.[ai.id],
        canEdit: actionItemClip?.canEdit ?? false,
      };
      return clip;
    })
  );

  const handleChangeStatus = (newStatus: ActionItemClipStatus) => {
    if (!selectedActionItem) return;

    const operation: UpdateClipOperation = {
      id: selectedActionItem.id,
      sessionId: selectedActionItem.sessionId,
      parentClipId: selectedActionItem.id,
      status: newStatus,
      type: "action_item",
    };

    updateClip.mutate(operation, {
      onSuccess: () => {
        if (newStatus === "user_reject") {
          toast.success("Your feedback has been recorded.");
        }
      },
      onError: () => {
        toast.error(
          "Something went wrong and your changes could not be saved."
        );
      },
    });

    setSelectedActionItem(null);
    onCloseStatusMenu();
  };

  return (
    <>
      <Column sx={{ px: 3 }} spacing={1}>
        <TableSubHeading variant="apart">
          <Row variant="left" spacing={1}>
            <IconHeading type="action_items" shape="square" size="small">
              Action Items
            </IconHeading>
            <TooltipIcon
              title="Read predicts the status of an action item based on past and future meetings."
              placement="bottom"
              variant="help"
            />
            {!isEmpty(actionItemsToShow) && (
              <CountChip>{actionItemsToShow.length}</CountChip>
            )}
          </Row>
          <ViewToggle value={filterBy} onChange={handleToggleSelection}>
            <ToggleButton value="me">For me</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ViewToggle>
        </TableSubHeading>
        <Column spacing={1}>
          {isEmpty(actionItemsToShow) ? (
            <Text light>
              No action items{filterBy === "me" && " assigned to you"}
            </Text>
          ) : (
            <BulletedList>
              <ExpandableList max={3} buttonProps={{ sx: { ml: 0 } }}>
                {actionItemsToShow.map((ai) => (
                  <ActionItemWithStatus
                    key={ai.id}
                    item={ai}
                    onClickChip={(e) => {
                      onClickStatusChip(e);
                      setSelectedActionItem(ai);
                    }}
                  />
                ))}
              </ExpandableList>
            </BulletedList>
          )}
        </Column>
      </Column>
      <ChangeStatusMenu
        open={statusMenuOpen}
        anchorEl={anchorElement}
        onClose={() => {
          setSelectedActionItem(null);
          onCloseStatusMenu();
        }}
        onChangeStatus={handleChangeStatus}
        status={selectedActionItem?.status as ActionItemClipStatus}
      />
    </>
  );
};

export default ActionItems;
